import { useApolloClient } from "@apollo/client";
import { useAuth } from "@clerk/nextjs";
import { isEmpty } from "lodash";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";
import { FeaturePermission } from "@/__generated__/featurePermissions";
import { AuthUser, mapUserData } from "@/auth/helpers";
import {
  UserQuery,
  UserQueryResult,
  useUserLazyQuery,
} from "@/graphql/queries/user.graphql.types";
import { useSelectiveEffect } from "@/hooks/misc/useSelectiveEffect";
import { useTracking } from "@/hooks/observability/useTracking";
import { UrlPermission } from "@/types/urlPermission";
import { getDefaultRoute } from "@/utils/getDefaultRoute";
import { hasPermissionsToAccessUrl } from "@/utils/permissionUtils";
import useErrorLogger from "@/utils/useErrorLogger";
import { LoginError } from "@/views/landingView";

export type UserMedspa = UserMedspas[number];

type UserMedspas = UserQueryResult["data"]["userMedspa"];
type UseAuthStateResult = {
  user: AuthUser | null;
  userMedspaData: UserQuery | null;
  activeUserMedspa: UserMedspa | null;
  isLoading: boolean;
  error: LoginError | null;
  urlPermissions: string[];
  hasUrlPermission: (url: UrlPermission) => boolean;
  hasFeaturePermission: (permission: FeaturePermission) => boolean;
  hasAnyOfFeaturePermissions: (permissions: FeaturePermission[]) => boolean;
  onMedspaChange: (medspaId: string) => void;
};

const checkUserForErrors = (user: AuthUser): LoginError | null => {
  if (!user) {
    return {
      title: "Couldn't decode the user token",
      description: "Please contact Moxie support.",
    };
  }

  if (!user.id) {
    // When user id does not exist, it means clerk user id
    // is not associated with any user in our database
    return {
      title: "No account exists for this user",
      description: "Please contact Moxie support.",
    };
  }

  if (user.role === null) {
    // When user role is null, it means the default role metadata for the user in clerk is not set
    return {
      title: "There is an issue with the account configuration for this user",
      description: "Please contact Moxie support.",
    };
  }

  return null;
};

const checkUserMedspaForErrors = (
  userMedspaData: UserQueryResult
): LoginError => {
  if (!userMedspaData?.data) {
    return {
      title: "Something went wrong",
      description: "Please contact Moxie support.",
    };
  }

  if (isEmpty(userMedspaData.data.userMedspa)) {
    return {
      title: "This account is not connected to any medspa",
      description:
        "Please contact your provider success manager to get access to Moxie Suite.",
    };
  }

  return null;
};

export const useAuthState = (): UseAuthStateResult => {
  const logError = useErrorLogger();
  const apollo = useApolloClient();
  const router = useRouter();
  const { isLoaded: isClerkLoaded, isSignedIn, userId, getToken } = useAuth();
  // Track previous user id to detect account switch / logout
  const [previousUserId, setPreviousUserId] = useState<string | null>(null);

  const [user, setUser] = useState<AuthUser>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{
    title: string;
    description: string;
  } | null>(null);

  const [activeUserMedspa, setActiveUserMedspa] = useState<UserMedspa>(null);

  const [getUserMedspaData, { data: userMedspaData }] = useUserLazyQuery();

  useTracking(user, activeUserMedspa);

  useSelectiveEffect(() => {
    if (!isClerkLoaded) return;

    const accountSwitch = previousUserId && previousUserId !== userId;
    const isLogout = !isSignedIn && !userId;

    if (accountSwitch || isLogout) {
      setUser(null);
      setActiveUserMedspa(null);
      setError(null);
      setLoading(false);
      // If we use resetStore, all queries will be refetched, even if the token is missing (e.g. when user logs out or session expires)
      // This is not what we want, so we use clearStore instead. We should consider extending the ApolloLink to handle this case.
      apollo.clearStore();
    }

    if (isLogout && !router.pathname.startsWith("/sign-in")) {
      router.push("/sign-in");
    }
    // I don't want to run this effect when router changes (router.path update also updates the router object),
    // since some pages use redirect strategy and this can cause infinite loop
  }, [isClerkLoaded, apollo, previousUserId, userId, isSignedIn]);

  useSelectiveEffect(() => {
    if (!isClerkLoaded) return;

    const fetchUser = async () => {
      setLoading(true);

      try {
        const userData = await mapUserData(getToken);

        const userError = checkUserForErrors(userData);
        if (userError) {
          setError(userError);

          return;
        }

        const userMedspaDataResult = await getUserMedspaData({
          variables: { id: userData.id },
        });

        const userMedspaError = checkUserMedspaForErrors(userMedspaDataResult);

        if (userMedspaError) {
          setError(userMedspaError);

          return;
        }
        const getUserMedspa = (userMedspa: UserMedspa) =>
          userMedspa.medspa.id === router.query.medspaId;

        const userMedspa = userMedspaDataResult.data.userMedspa;
        const selectedUserMedspa =
          userMedspa.find(getUserMedspa) || userMedspa[0];

        const fullUserData: AuthUser = {
          ...userData,
          firstName: selectedUserMedspa.user.firstName,
          lastName: selectedUserMedspa.user.lastName,
          displayName: selectedUserMedspa.user.fullName,
          newRole: selectedUserMedspa.userMedspaRole?.name,
        };

        setUser(fullUserData);
        setActiveUserMedspa(selectedUserMedspa);
        setPreviousUserId(userId);

        if (router.query.medspaId !== selectedUserMedspa.medspa.id) {
          // Don't redirect if the user is already on the correct page (medspa matches)
          // For example, user entered the page manually, via email link, etc.
          // WARNING: This line is responsible for redirecting user after sign in!!
          router.push(getDefaultRoute(selectedUserMedspa));
        }
      } catch (e) {
        setError({
          title: "Something went wrong",
          description: "Please try again later.",
        });

        logError(e);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      setUser(null);
      setActiveUserMedspa(null);
      setError(null);

      fetchUser();
    }

    // I don't want to run this effect when router changes (router.path update also updates the router object),
    // since some pages use redirect strategy and this can cause infinite loop
  }, [isClerkLoaded, userId, logError, getUserMedspaData, getToken]);

  const handleMedspaChange = useCallback(
    (medspaId: string) => {
      setActiveUserMedspa(
        userMedspaData.userMedspa.find((um) => um.medspa.id === medspaId) ||
          userMedspaData.userMedspa[0]
      );
    },
    [userMedspaData]
  );

  const accountSwitch = previousUserId && previousUserId !== userId;

  const urlPermissions = useMemo(
    () => activeUserMedspa?.urlPermissions.map((up) => up.urlPattern),
    [activeUserMedspa?.urlPermissions]
  );
  const featurePermissions = useMemo(
    () => activeUserMedspa?.featurePermissions.map((fp) => fp.codename),
    [activeUserMedspa?.featurePermissions]
  );

  const hasFeaturePermission = useCallback(
    (permission: FeaturePermission) => featurePermissions.includes(permission),
    [featurePermissions]
  );

  const hasAnyOfFeaturePermissions = useCallback(
    (permissions: FeaturePermission[]) => {
      const userPermissionsSet = new Set(featurePermissions);

      return permissions.some((permission) =>
        userPermissionsSet.has(permission)
      );
    },
    [featurePermissions]
  );

  if (!isSignedIn || accountSwitch || !activeUserMedspa) {
    return {
      user: null,
      userMedspaData: null,
      activeUserMedspa: null,
      isLoading: loading,
      error: null,
      urlPermissions: [],
      hasUrlPermission: () => false,
      hasFeaturePermission: () => false,
      hasAnyOfFeaturePermissions: () => false,
      onMedspaChange: handleMedspaChange,
    };
  }

  return {
    user,
    userMedspaData,
    activeUserMedspa,
    isLoading: loading,
    error,
    urlPermissions,
    hasUrlPermission: (url: UrlPermission) =>
      hasPermissionsToAccessUrl(url, urlPermissions),
    hasFeaturePermission,
    hasAnyOfFeaturePermissions,
    onMedspaChange: handleMedspaChange,
  };
};
